import {
  AI_BRIDGE_SOURCE,
  AI_BRIDGE_TYPE,
  AiActionPayload,
  AiRoutePayload,
  buildRoutePath,
  buildRouteStatePayload,
  getIframeInnerPath,
  isAiBridgeMessage,
  isAllowedAiBridgeOrigin,
  isValidRoutePath,
  parseActionPayload,
  parseRoutePayload,
} from './protocol';

export type IframeAiBridgeOptions = {
  /**
   * 收到合法 ROUTE 后的业务回调。
   * 第一个参数为合并 query 后的最终 path（可供 history.push）。
   */
  onRoute?: (finalPath: string, payload: AiRoutePayload, event: MessageEvent) => void;
  /** 收到合法 ACTION 后的业务回调（建议写入全局 store） */
  onAction?: (payload: AiActionPayload, event: MessageEvent) => void;
  /**
   * 自定义当前内部 path 读取（须与 ROUTE path 同形态）。
   * 默认走 Hash 解析；接入方可传入 `pathname + search`（如 react-router location）。
   */
  getInnerPath?: () => string;
  /** 是否校验 event.origin；默认 true */
  checkOrigin?: boolean;
  /** 自定义 origin 校验；默认 *.hwwt2.com */
  isOriginAllowed?: (origin: string) => boolean;
  /**
   * 连续 ROUTE 之间的最小间隔（ms），避免列表→详情连续 push 被 CacheRoute 冲掉。
   * 默认 200；设为 0 则仍串行但不额外等待。
   */
  routeSerialGapMs?: number;
};

/** 连续两次 history.push 之间的默认间隔 */
const DEFAULT_ROUTE_SERIAL_GAP_MS = 200;

function replyTargetOrigin(event: MessageEvent): string {
  return event.origin && event.origin !== 'null' ? event.origin : '*';
}

function replyPong(event: MessageEvent, echoPayload: any) {
  parent.postMessage(
    {
      source: AI_BRIDGE_SOURCE.FRANCHISEE,
      type: AI_BRIDGE_TYPE.PONG,
      payload: {
        echo: echoPayload,
        href: window.location.href,
        at: Date.now(),
      },
    },
    replyTargetOrigin(event)
  );
}

function replyRouteState(event: MessageEvent, path: string, requestId?: string) {
  parent.postMessage(
    {
      source: AI_BRIDGE_SOURCE.FRANCHISEE,
      type: AI_BRIDGE_TYPE.ROUTE_STATE,
      payload: buildRouteStatePayload(path, requestId),
    },
    replyTargetOrigin(event)
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * iframe（franchisee）侧接入：监听助手 PING / ROUTE / ACTION / GET_ROUTE。
 * - ROUTE / PING / ACTION → 回 PONG
 * - GET_ROUTE → 同步回 ROUTE_STATE（供助手短路）
 * - 连续 ROUTE 串行执行（先 push 再 PONG，条间留 gap）
 * 见 src/bridge/ai-navigate-scheme.md §7
 */
export function startIframeAiBridge(
  optionsOrNavigate?: IframeAiBridgeOptions | ((path: string) => void)
): () => void {
  const options: IframeAiBridgeOptions =
    typeof optionsOrNavigate === 'function'
      ? {
          onRoute: (finalPath) => {
            optionsOrNavigate(finalPath);
          },
        }
      : optionsOrNavigate || {};

  const checkOrigin = options.checkOrigin !== false;
  const isOriginAllowed = options.isOriginAllowed || isAllowedAiBridgeOrigin;
  const resolveInnerPath = options.getInnerPath || getIframeInnerPath;
  const routeSerialGapMs =
    typeof options.routeSerialGapMs === 'number'
      ? Math.max(0, options.routeSerialGapMs)
      : DEFAULT_ROUTE_SERIAL_GAP_MS;

  /** 串行队列：保证列表→详情按序 push，且 PONG 在真正 onRoute 之后 */
  let routeChain: Promise<void> = Promise.resolve();

  const enqueueRoute = (task: () => void): void => {
    routeChain = routeChain
      .then(async () => {
        task();
        if (routeSerialGapMs > 0) {
          await delay(routeSerialGapMs);
        }
      })
      .catch((err) => {
        console.warn('[aiBridge][iframe] ROUTE 队列执行异常', err);
      });
  };

  const onMessage = (event: MessageEvent) => {
    const { data } = event;
    if (!isAiBridgeMessage(data)) {
      return;
    }
    if (data.source !== AI_BRIDGE_SOURCE.ASSISTANT) {
      return;
    }
    if (checkOrigin && !isOriginAllowed(event.origin)) {
      console.warn('[aiBridge][iframe] 拒绝：origin 不在白名单', event.origin);
      return;
    }

    if (data.type === AI_BRIDGE_TYPE.GET_ROUTE) {
      const requestId =
        data.payload &&
        typeof data.payload === 'object' &&
        typeof (data.payload as { requestId?: unknown }).requestId === 'string'
          ? (data.payload as { requestId: string }).requestId
          : undefined;
      const path = resolveInnerPath();
      console.log('[aiBridge][iframe] GET_ROUTE → ROUTE_STATE', path, requestId);
      replyRouteState(event, path, requestId);
      return;
    }

    if (data.type === AI_BRIDGE_TYPE.ACTION) {
      const actionPayload = parseActionPayload(data.payload);
      console.log('[aiBridge][iframe] ACTION', actionPayload || data.payload);
      if (actionPayload) {
        options.onAction?.(actionPayload, event);
      } else {
        console.warn('[aiBridge][iframe] ACTION 无有效 action，跳过', data.payload);
      }
      replyPong(event, data.payload);
      return;
    }

    if (data.type === AI_BRIDGE_TYPE.ROUTE) {
      const route = parseRoutePayload(data.payload);
      console.log('[aiBridge][iframe] ROUTE', route || data.payload);

      if (route?.action) {
        // 一期仅预留：不执行 action 业务（请改用顶层 ACTION）
        console.log('[aiBridge][iframe] action 预留（未执行）', route.action, route);
      }

      if (route) {
        const finalPath = buildRoutePath(route);
        if (isValidRoutePath(finalPath)) {
          enqueueRoute(() => {
            console.log('[aiBridge][iframe] ROUTE 执行跳转', finalPath);
            options.onRoute?.(finalPath, route, event);
            // PONG 在真正 push 之后，便于助手侧 waitForPong 对齐
            replyPong(event, data.payload);
          });
        } else {
          console.warn('[aiBridge][iframe] 非法 path，跳过跳转', finalPath);
          replyPong(event, data.payload);
        }
      } else {
        console.warn('[aiBridge][iframe] ROUTE 无有效 path/content，跳过跳转', data.payload);
        replyPong(event, data.payload);
      }
      return;
    }

    if (data.type === AI_BRIDGE_TYPE.PING) {
      replyPong(event, data.payload);
    }
  };

  window.addEventListener('message', onMessage);
  return () => {
    window.removeEventListener('message', onMessage);
  };
}
